import { CompactAudioRefInstance } from '@doltech/ui/lib/figma/Audio/Player/CompactAudioPlayer';
import { colorsV2 } from '@doltech/ui/lib/figma/colors-v2';
import { Typography } from '@doltech/ui/lib/figma/DesignSystemV2/Typography';
import { SectionOfContent } from '@doltech/ui/lib/figma/Layout/Landing/Content/SectionOfContent';
import { Separator } from '@doltech/ui/lib/figma/Layout/Landing/Content/Separator';
import { deviceBreakpoints } from '@doltech/ui/lib/hooks/useDeviceQuery.hook';
import { SkillEnum, TestTypeEnum } from '@doltech/utils/lib/constants';
import { getPreviewAudio } from '@doltech/utils/lib/location';
import { getLandingUrlFromPath } from '@doltech/utils/lib/url';
import { observer } from 'mobx-react-lite';
import dynamic from 'next/dynamic';
import * as React from 'react';
import { useInView } from 'react-cool-inview';
import withHydrationOnDemand from '@doltech/core/lib/hooks/withDolHydrationOnDemand';
import styled from 'styled-components';

import { useLandingSeoPageDataStore } from '../landing-home/model/landingPageData.store';
import { TranscriptSection } from '../view-transcript/components/TranscriptSection';
import { GroupQuestionsSubject } from './components/TestContent';
import { useViewTestSubjectStore } from './model/view-test-subject.hook';
import {
  ButtonDoTestSection,
  ContentLayout,
  SectionHeader,
  ViewSolutionSection,
} from './SubjectReadingContent';

const ReplayableAudioPlayer = dynamic<any>(
  import('@doltech/ui/lib/figma/Audio/Player/ReplayableAudioPlayer').then(
    (mod) => mod.ReplayableAudioPlayer
  ),
  { ssr: false }
);

const Main = styled(ContentLayout)`
  .audio-container {
    margin-top: 16px;
    padding: 8px 24px;
    background-color: ${colorsV2.gray5};

    @media ${deviceBreakpoints.mobile} {
      padding: 8px 16px;
    }
  }
`;

const TranscriptSectionNoHydrate = withHydrationOnDemand()(TranscriptSection);
const GroupQuestionsSubjectNoHydrate = withHydrationOnDemand()(GroupQuestionsSubject);

interface ListeningSectionProps {
  testSection: any;
  sectionIndex: number;
  isPdfContent: boolean;
  tableOfContent: any;
  testType: TestTypeEnum;
}

const ListeningSection = observer(
  ({
    testSection,
    sectionIndex,
    isPdfContent,
    tableOfContent,
    testType,
  }: ListeningSectionProps) => {
    const landingSeoPageDataStore = useLandingSeoPageDataStore();
    const doTestLink = landingSeoPageDataStore.getDoTestLink();
    const audioRef = React.useRef<CompactAudioRefInstance>();
    const { observe } = useInView({
      threshold: [0.2, 0.8],
      onLeave: () => {
        if (audioRef?.current?.pause) {
          audioRef.current.pause();
        }
      },
    });

    const isFullTest = TestTypeEnum.FULL_TEST === testType;
    const fontVariant = isFullTest ? 'medium/24' : 'semi-bold/32';

    const renderTitle = React.useCallback(
      (title) => (
        <Typography.Title className="font-inter" variant={fontVariant} level={isFullTest ? 3 : 2}>
          {title}
        </Typography.Title>
      ),
      [fontVariant, isFullTest]
    );

    return (
      <div ref={observe as any} className="test-section-container">
        {isFullTest && <SectionHeader index={sectionIndex} skill={SkillEnum.LISTENING} />}

        <SectionOfContent
          className="test-section-content"
          id={`test-section-${sectionIndex}`}
          sectionId={`test-section-${sectionIndex}`}
          tableOfContent={tableOfContent}
          renderTitle={renderTitle}
        >
          <div className="audio-container">
            <ReplayableAudioPlayer
              ref={audioRef}
              url={getPreviewAudio(testSection?.script?.audio?.path)}
            />
          </div>
          <SectionOfContent.Content>
            {testSection.questionGroups.map((group) => {
              return (
                <GroupQuestionsSubjectNoHydrate
                  key={group.id}
                  currentSection={testSection}
                  group={group}
                  isPdfContent={isPdfContent}
                />
              );
            })}
          </SectionOfContent.Content>
        </SectionOfContent>

        <Separator mb="24px" variant="three-dot-horizontal" />

        <TranscriptSectionNoHydrate
          sectionName={testSection.name}
          script={testSection.script}
          type={testSection.script.type}
          key={sectionIndex}
          sectionIndex={sectionIndex}
          renderTitle={renderTitle}
        />

        <ButtonDoTestSection doTestLink={getLandingUrlFromPath(doTestLink)} />

        <Separator my="24px" variant="three-dot-horizontal" />

        <ViewSolutionSection sectionIndex={sectionIndex - 1} renderTitle={renderTitle} />

        <Separator my="24px" variant="three-dot-horizontal" />
      </div>
    );
  }
);

export interface SubjectListeningContentProps {
  isPdfContent?: boolean;
}

export const SubjectListeningContent = observer((props?: SubjectListeningContentProps) => {
  const store = useViewTestSubjectStore();
  const tableOfContent = store.getTableOfContent();
  const { isPdfContent } = props;
  return (
    <Main>
      {store.testSections.map((testSection, testSectionIndex) => {
        const index = testSectionIndex + 1;

        return (
          <ListeningSection
            key={index}
            testSection={testSection}
            sectionIndex={index}
            isPdfContent={isPdfContent}
            tableOfContent={tableOfContent}
            testType={store.testType}
          />
        );
      })}
    </Main>
  );
});
